<script setup lang="ts">
import { ref } from "vue"
import { useMaskedValue } from "@/composables/useMaskedValue"
import { formatZipCode, normalizeZipCode } from "@/lib/masks"
import UiFieldWrapper from "./UiFieldWrapper.vue"

type PostalCodeAddress = {
  zip_code: string
  street: string
  district: string
  city: string
  state: string
}

const model = defineModel<string>({ default: "" })

const emit = defineEmits<{
  "lookup-success": [address: PostalCodeAddress]
  "lookup-error": [message: string]
}>()

const props = withDefaults(
  defineProps<{
    id: string
    label?: string
    placeholder?: string
    required?: boolean
    disabled?: boolean
    error?: string
    helperText?: string
    lookupEnabled?: boolean
    showLookupButton?: boolean
    lookupOnBlur?: boolean
  }>(),
  {
    label: "CEP",
    placeholder: "00000-000",
    required: false,
    disabled: false,
    error: "",
    helperText: "",
    lookupEnabled: true,
    showLookupButton: true,
    lookupOnBlur: true,
  }
)

const isLookingUp = ref(false)
const lookupMessage = ref("")
const lastLookupCep = ref("")

const { displayValue, handleInput } = useMaskedValue(
  () => model.value,
  (_event, value) => {
    model.value = value
  },
  formatZipCode,
  normalizeZipCode
)

async function lookupPostalCode(force = false): Promise<void> {
  if (!props.lookupEnabled || props.disabled || isLookingUp.value) return

  const cep = normalizeZipCode(model.value)
  if (cep.length !== 8) {
    lookupMessage.value = ""
    return
  }

  if (!force && lastLookupCep.value === cep) return

  isLookingUp.value = true
  lookupMessage.value = ""

  try {
    const response = await fetch(`/postal_codes/${cep}`, {
      headers: {
        Accept: "application/json",
      },
    })

    const payload = await response.json()

    if (!response.ok) {
      const message = String(payload.error || "Nao foi possivel buscar o CEP")
      lookupMessage.value = message
      emit("lookup-error", message)
      return
    }

    lastLookupCep.value = cep
    const address = payload.address as PostalCodeAddress
    lookupMessage.value = "Endereco encontrado"
    emit("lookup-success", address)
  } catch {
    const message = "Falha na comunicacao ao buscar CEP"
    lookupMessage.value = message
    emit("lookup-error", message)
  } finally {
    isLookingUp.value = false
  }
}

function onInput(event: Event): void {
  handleInput(event)
  lookupMessage.value = ""
}

function onBlur(): void {
  if (!props.lookupOnBlur) return
  void lookupPostalCode(false)
}

function onLookupButtonClick(): void {
  void lookupPostalCode(true)
}
</script>

<template>
  <UiFieldWrapper :label="props.label" :for-id="props.id" :error="props.error" :helper-text="props.helperText">
    <div class="space-y-1">
      <div class="flex gap-2">
        <input
          :id="props.id"
          :value="displayValue"
          :placeholder="props.placeholder"
          :required="props.required"
          :disabled="props.disabled"
          inputmode="numeric"
          class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
          @input="onInput"
          @blur="onBlur"
        />
        <button
          v-if="props.showLookupButton"
          type="button"
          :disabled="props.disabled || isLookingUp"
          class="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          @click="onLookupButtonClick"
        >
          {{ isLookingUp ? "Buscando..." : "Buscar" }}
        </button>
      </div>
      <p v-if="lookupMessage" class="text-xs" :class="lookupMessage === 'Endereco encontrado' ? 'text-emerald-600' : 'text-amber-600'">
        {{ lookupMessage }}
      </p>
    </div>
  </UiFieldWrapper>
</template>

